import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Loader2, ExternalLink, ArrowUp, ArrowDown,
  ChevronRight, FolderPlus, Link2,
} from "lucide-react";

// Curated emoji set the admin can pick from. Anything else can still be
// pasted manually into the icon field.
const SUGGESTED_ICONS = [
  "🕊️", "🧸", "🎓", "♿", "🗣️", "⚡", "🧭", "🛠️", "🩺", "🏠",
  "🏡", "🌍", "🧠", "🥗", "🛂", "🏛️", "👵", "🧓", "💻", "🚐",
  "💼", "💧", "📚", "🤝", "❤️", "🍎", "🚸", "📞", "🕌", "🌱",
];

export default function CommunityResourcesAdmin() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [editingCat, setEditingCat] = useState(null);   // {id?, title, icon, slug, order}
  const [editingItem, setEditingItem] = useState(null); // {catId, item}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/community-resources");
      setCats(r.data || []);
      if (!activeId && r.data?.length) setActiveId(r.data[0].id);
    } catch {
      toast.error("Failed to load community resources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activeCat = useMemo(
    () => cats.find((c) => c.id === activeId) || null,
    [cats, activeId]
  );

  // ---------- Category actions ----------
  const newCat = () => setEditingCat({ title: "", icon: "🔗", slug: "", order: (cats.length + 1) * 10, items: [] });
  const editCat = (c) => setEditingCat({ id: c.id, title: c.title, icon: c.icon, slug: c.slug, order: c.order });
  const deleteCat = async (c) => {
    if (!window.confirm(`Delete category "${c.title}" and all ${c.items?.length || 0} items inside?`)) return;
    try {
      await api.delete(`/admin/community-resources/${c.id}`);
      toast.success("Category deleted");
      if (activeId === c.id) setActiveId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };
  const moveCat = async (c, dir) => {
    const sorted = [...cats].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === c.id);
    const target = sorted[idx + dir];
    if (!target) return;
    try {
      await Promise.all([
        api.put(`/admin/community-resources/${c.id}`, { title: c.title, icon: c.icon, slug: c.slug, order: target.order, items: [] }),
        api.put(`/admin/community-resources/${target.id}`, { title: target.title, icon: target.icon, slug: target.slug, order: c.order, items: [] }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  // ---------- Item actions ----------
  const newItem = (catId) => setEditingItem({ catId, item: { title: "", url: "", desc: "", sub: [], order: 0 } });
  const editItem = (catId, item) => setEditingItem({ catId, item: { ...item, sub: item.sub || [] } });
  const deleteItem = async (catId, item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/community-resources/${catId}/items/${item.id}`);
      toast.success("Item deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-ink/50">
        <Loader2 className="animate-spin" /> <span className="ml-2">Loading…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="community-resources-admin">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-ink">Community Resources</h1>
          <p className="text-sm text-ink/60 mt-1 max-w-2xl">
            Manage the directory shown at <a href="/community-resources" target="_blank" rel="noreferrer noopener" className="text-navy underline-offset-2 hover:underline">/community-resources</a>.
            Add categories, edit existing items, and group sub-links inside any resource.
          </p>
        </div>
        <button
          onClick={newCat}
          data-testid="cr-add-category-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-cream text-sm font-medium hover:bg-navy/90 shadow-sm"
        >
          <FolderPlus size={16} /> Add Category
        </button>
      </div>

      {/* Two-pane layout */}
      <div className="grid lg:grid-cols-[320px,1fr] gap-6">
        {/* LEFT — categories list */}
        <aside className="space-y-2" data-testid="cr-categories-list">
          {cats.sort((a, b) => a.order - b.order).map((c, idx) => {
            const active = c.id === activeId;
            return (
              <div
                key={c.id}
                className={`group rounded-xl border transition-all ${
                  active ? "bg-navy text-cream border-navy shadow-md" : "bg-white border-sand hover:border-navy/30"
                }`}
              >
                <button
                  onClick={() => setActiveId(c.id)}
                  data-testid={`cr-cat-${c.slug}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                >
                  <span className="text-xl leading-none w-6 text-center">{c.icon}</span>
                  <span className="flex-1 font-medium text-sm leading-tight">{c.title}</span>
                  <span className={`text-[10px] ${active ? "text-gold" : "text-ink/35"}`}>{c.items?.length || 0}</span>
                </button>
                <div className={`flex items-center gap-1 px-2 pb-2 ${active ? "" : "opacity-0 group-hover:opacity-100 transition-opacity"}`}>
                  <button title="Move up" disabled={idx === 0} onClick={() => moveCat(c, -1)}
                    className={`p-1.5 rounded ${active ? "hover:bg-cream/15 text-cream/80" : "hover:bg-sand/60 text-ink/60"} disabled:opacity-30`}>
                    <ArrowUp size={14} />
                  </button>
                  <button title="Move down" disabled={idx === cats.length - 1} onClick={() => moveCat(c, 1)}
                    className={`p-1.5 rounded ${active ? "hover:bg-cream/15 text-cream/80" : "hover:bg-sand/60 text-ink/60"} disabled:opacity-30`}>
                    <ArrowDown size={14} />
                  </button>
                  <div className="flex-1" />
                  <button title="Edit" onClick={() => editCat(c)}
                    className={`p-1.5 rounded ${active ? "hover:bg-cream/15 text-cream/80" : "hover:bg-sand/60 text-ink/60"}`}>
                    <Pencil size={14} />
                  </button>
                  <button title="Delete" onClick={() => deleteCat(c)}
                    className={`p-1.5 rounded ${active ? "hover:bg-red-500/30 text-cream/80" : "hover:bg-red-50 text-red-600"}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </aside>

        {/* RIGHT — items editor */}
        <main className="min-w-0">
          {!activeCat ? (
            <div className="rounded-2xl border border-dashed border-sand bg-white p-12 text-center text-ink/50">
              <ChevronRight className="mx-auto mb-3" size={28} />
              Pick a category on the left to manage its items.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl bg-white border border-sand p-5 flex items-center gap-4">
                <span className="text-3xl leading-none">{activeCat.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-ink">{activeCat.title}</div>
                  <div className="text-xs text-ink/55 mt-0.5">/{activeCat.slug} · {activeCat.items?.length || 0} item(s)</div>
                </div>
                <button
                  onClick={() => newItem(activeCat.id)}
                  data-testid="cr-add-item-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-gold text-navy text-sm font-semibold hover:bg-gold/90"
                >
                  <Plus size={15} /> Add Item
                </button>
              </div>

              {(activeCat.items || []).length === 0 ? (
                <div className="rounded-2xl bg-white border border-dashed border-sand p-10 text-center text-ink/50 text-sm">
                  No items in this category yet.
                </div>
              ) : (
                <div className="space-y-3" data-testid="cr-items-list">
                  {[...activeCat.items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((it) => (
                    <div key={it.id} className="rounded-xl bg-white border border-sand p-4 hover:border-navy/30 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-ink leading-snug">{it.title}</h3>
                            {it.url && (
                              <a href={it.url} target="_blank" rel="noreferrer noopener"
                                className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
                                <ExternalLink size={11} /> open
                              </a>
                            )}
                          </div>
                          {it.desc && <p className="text-sm text-ink/65 mt-1 line-clamp-2">{it.desc}</p>}
                          {it.sub?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {it.sub.map((s, i) => (
                                <span key={i} className="text-[11px] bg-gold/15 text-navy px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                  <Link2 size={10} /> {s.label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => editItem(activeCat.id, it)} title="Edit"
                            data-testid={`cr-edit-item-${it.id}`}
                            className="p-1.5 rounded hover:bg-sand/60 text-ink/60">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deleteItem(activeCat.id, it)} title="Delete"
                            data-testid={`cr-delete-item-${it.id}`}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Category modal */}
      {editingCat && (
        <CategoryModal
          value={editingCat}
          onClose={() => setEditingCat(null)}
          onSaved={() => { setEditingCat(null); load(); }}
        />
      )}

      {/* Item modal */}
      {editingItem && (
        <ItemModal
          catId={editingItem.catId}
          value={editingItem.item}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); load(); }}
        />
      )}
    </div>
  );
}

/* ===================== Category Modal ===================== */
function CategoryModal({ value, onClose, onSaved }) {
  const [form, setForm] = useState({ title: value.title || "", icon: value.icon || "🔗", slug: value.slug || "", order: value.order || 0 });
  const [saving, setSaving] = useState(false);
  const isEdit = !!value.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.icon.trim()) { toast.error("Icon is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/community-resources/${value.id}`, { ...form, items: [] });
      } else {
        await api.post("/admin/community-resources", { ...form, items: [] });
      }
      toast.success(isEdit ? "Category updated" : "Category created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit Category" : "New Category"} testid="cr-cat-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title">
          <input
            type="text" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="cr-cat-title"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            placeholder="e.g. Aging"
            autoFocus
          />
        </Field>
        <Field label="Icon (emoji)" hint="Pick from below or paste any emoji.">
          <input
            type="text" value={form.icon} maxLength={8}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
            data-testid="cr-cat-icon"
            className="w-24 px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy text-2xl text-center"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SUGGESTED_ICONS.map((e) => (
              <button key={e} type="button" onClick={() => setForm({ ...form, icon: e })}
                className={`w-9 h-9 rounded-lg border text-lg transition-all ${
                  form.icon === e ? "bg-navy border-navy" : "bg-white border-sand hover:border-navy/40"
                }`}>
                {e}
              </button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="URL slug" hint="Optional. Auto-generated from title.">
            <input
              type="text" value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              data-testid="cr-cat-slug"
              className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
              placeholder="e.g. aging"
            />
          </Field>
          <Field label="Order" hint="Lower = shown first.">
            <input
              type="number" value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
              data-testid="cr-cat-order"
              className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            />
          </Field>
        </div>
        <ModalActions saving={saving} onClose={onClose} testid="cr-cat-save" />
      </form>
    </Modal>
  );
}

/* ===================== Item Modal ===================== */
function ItemModal({ catId, value, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: value.title || "",
    url: value.url || "",
    desc: value.desc || "",
    sub: value.sub || [],
    order: value.order || 0,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!value.id;

  const addSub = () => setForm({ ...form, sub: [...form.sub, { label: "", url: "" }] });
  const updateSub = (i, key, val) => {
    const next = [...form.sub];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, sub: next });
  };
  const removeSub = (i) => setForm({ ...form, sub: form.sub.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    // Filter out empty sub-link rows
    const cleanSub = form.sub.filter((s) => (s.label || "").trim() && (s.url || "").trim());
    setSaving(true);
    try {
      const body = { ...form, sub: cleanSub };
      if (isEdit) {
        await api.put(`/admin/community-resources/${catId}/items/${value.id}`, body);
      } else {
        await api.post(`/admin/community-resources/${catId}/items`, body);
      }
      toast.success(isEdit ? "Item updated" : "Item created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit Resource" : "New Resource"} testid="cr-item-modal" wide>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title">
          <input
            type="text" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="cr-item-title"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            placeholder="e.g. Senior Centers List"
            autoFocus
          />
        </Field>
        <Field label="URL" hint="Optional. Leave blank for info-only entries (phone/address etc.).">
          <input
            type="url" value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            data-testid="cr-item-url"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            placeholder="https://example.org/page"
          />
        </Field>
        <Field label="Description" hint="Short context. Phone/address with emoji icons (📞 📍) is OK.">
          <textarea
            value={form.desc} rows={4}
            onChange={(e) => setForm({ ...form, desc: e.target.value })}
            data-testid="cr-item-desc"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            placeholder="What this resource offers, who's eligible, contact info."
          />
        </Field>
        <Field label="Sub-links" hint="Optional related links (e.g. an Apply button + a Find page).">
          <div className="space-y-2">
            {form.sub.map((s, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  type="text" value={s.label} placeholder="Label (e.g. Apply)"
                  onChange={(e) => updateSub(i, "label", e.target.value)}
                  className="w-40 px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy text-sm"
                />
                <input
                  type="url" value={s.url} placeholder="https://…"
                  onChange={(e) => updateSub(i, "url", e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy text-sm"
                />
                <button type="button" onClick={() => removeSub(i)}
                  className="p-2 rounded hover:bg-red-50 text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addSub}
              data-testid="cr-item-add-sub"
              className="inline-flex items-center gap-1.5 text-xs text-navy hover:text-navy/70 font-medium">
              <Plus size={13} /> Add sub-link
            </button>
          </div>
        </Field>
        <Field label="Order" hint="Lower = shown first inside this category.">
          <input
            type="number" value={form.order}
            onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
            data-testid="cr-item-order"
            className="w-32 px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
          />
        </Field>
        <ModalActions saving={saving} onClose={onClose} testid="cr-item-save" />
      </form>
    </Modal>
  );
}

/* ===================== Shared modal primitives ===================== */
function Modal({ onClose, title, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} mt-12 mb-12 shadow-2xl`}>
        <div className="flex items-center justify-between p-5 border-b border-sand">
          <h3 className="font-serif text-xl text-ink">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-sand/60 text-ink/60">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-ink/55 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-ink/45 mt-1">{hint}</p>}
    </div>
  );
}

function ModalActions({ saving, onClose, testid }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button type="button" onClick={onClose}
        className="px-4 py-2 rounded-lg text-sm text-ink/70 hover:bg-sand/60">
        Cancel
      </button>
      <button type="submit" disabled={saving}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-cream text-sm font-medium hover:bg-navy/90 disabled:opacity-60">
        {saving && <Loader2 size={14} className="animate-spin" />}
        Save
      </button>
    </div>
  );
}
